import type { AdminPnlFilters, AdminPnlPreview, AdminPnlReviewer, AdminPnlRow } from "../models/admin-pnl.models";
import {
  getAdminPnlErrorMessage,
  getAdminPnlRows,
  previewAdminPnlSync,
  syncAdminPnlNames,
} from "../services/admin-pnl.service";
import { ADMIN_PNL_INITIAL_FILTERS } from "../utils/admin-pnl.constants";
import { getAdminPnlSyncMessage, toAdminPnlRows } from "../utils/admin-pnl.utils";
import { useCallback, useEffect, useRef, useState } from "react";

export function useAdminPnl() {
  const [rows, setRows] = useState<AdminPnlRow[]>([]);
  const [reviewers, setReviewers] = useState<AdminPnlReviewer[]>([]);
  const [appliedFilters, setAppliedFilters] = useState(ADMIN_PNL_INITIAL_FILTERS);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [preview, setPreview] = useState<AdminPnlPreview | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const mounted = useRef(false);
  const writeInProgress = useRef(false);
  const previewInProgress = useRef(false);
  const readController = useRef<AbortController | null>(null);
  const previewController = useRef<AbortController | null>(null);

  const refresh = useCallback(async (filters: AdminPnlFilters, preserveMessage = false) => {
    readController.current?.abort();
    const controller = new AbortController();
    readController.current = controller;
    setLoading(true);
    setErrorMessage(null);
    if (!preserveMessage) setActionMessage(null);
    try {
      const result = await getAdminPnlRows(filters, controller.signal);
      if (!controller.signal.aborted) {
        setRows(toAdminPnlRows(result.rows));
        setReviewers(result.reviewers);
        setTruncated(result.truncated);
        setAppliedFilters(filters);
      }
    } catch (error) {
      if (!controller.signal.aborted) setErrorMessage(getAdminPnlErrorMessage(error));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void refresh(ADMIN_PNL_INITIAL_FILTERS);
    return () => {
      mounted.current = false;
      readController.current?.abort();
      previewController.current?.abort();
    };
  }, [refresh]);

  const selectSync = useCallback(
    async (row: AdminPnlRow) => {
      if (previewInProgress.current || writeInProgress.current || !row.CAN_SYNC) return;
      previewInProgress.current = true;
      const controller = new AbortController();
      previewController.current = controller;
      setPreviewing(true);
      setErrorMessage(null);
      setActionMessage(null);
      setModalError(null);
      try {
        const result = await previewAdminPnlSync(
          {
            tcCode: row.TC_CODE,
            voyageNo: row.VOYAGE_NO,
            reviewerEmail: appliedFilters.reviewerEmail,
          },
          controller.signal,
        );
        if (!controller.signal.aborted) setPreview(result);
      } catch (error) {
        if (!controller.signal.aborted) setErrorMessage(getAdminPnlErrorMessage(error));
      } finally {
        previewInProgress.current = false;
        if (mounted.current) setPreviewing(false);
      }
    },
    [appliedFilters],
  );

  const closePreview = useCallback(() => {
    if (!writeInProgress.current) {
      setPreview(null);
      setModalError(null);
    }
  }, []);

  const confirmSync = useCallback(async () => {
    if (!preview || modalError || writeInProgress.current) return;
    writeInProgress.current = true;
    setSyncing(true);
    setModalError(null);
    try {
      const result = await syncAdminPnlNames({ ...preview.target, previewToken: preview.previewToken });
      if (mounted.current) {
        setPreview(null);
        setActionMessage(getAdminPnlSyncMessage(result));
        await refresh(appliedFilters, true);
      }
    } catch (error) {
      if (mounted.current)
        setModalError(`${getAdminPnlErrorMessage(error)} Close this dialog and refresh before previewing again.`);
    } finally {
      writeInProgress.current = false;
      if (mounted.current) setSyncing(false);
    }
  }, [preview, modalError, appliedFilters, refresh]);

  return {
    rows,
    reviewers,
    appliedFilters,
    truncated,
    loading,
    previewing,
    syncing,
    preview,
    errorMessage,
    modalError,
    actionMessage,
    refresh,
    selectSync,
    closePreview,
    confirmSync,
  };
}
